import { useState } from 'react';
import { CodeBlock, Tabs } from '../../components/ui';
import { Callout, DocSection, DocTable } from '../../components/DocBlocks';
import { API_KEY_ENV, API_ORIGIN, BASE_URL } from '../../snippets';
import { AppLink, DocLink, PageHead, useDocs } from './context';

const KEY = 'aia_live_xxxxxxxxxxxxxxxx';

/** Yes/no table cell. */
const ok = (yes: boolean, note: string) => (
  <>
    <b>{yes ? 'Ya' : 'Belum'}</b> · {note}
  </>
);

/* Claude Code */

const CC_SETUP = [
  { id: 'shell', label: 'Terminal' },
  { id: 'settings', label: 'settings.json' },
] as const;
type CcSetup = (typeof CC_SETUP)[number]['id'];

function claudeCodeSnippet(id: CcSetup, model: string, small: string): string {
  if (id === 'settings') {
    return JSON.stringify(
      {
        env: {
          ANTHROPIC_BASE_URL: API_ORIGIN,
          ANTHROPIC_AUTH_TOKEN: KEY,
          ANTHROPIC_MODEL: model,
          ANTHROPIC_DEFAULT_HAIKU_MODEL: small,
        },
      },
      null,
      2,
    );
  }
  return [
    '# tambahkan ke ~/.zshrc atau ~/.bashrc',
    `export ANTHROPIC_BASE_URL="${API_ORIGIN}"`,
    `export ANTHROPIC_AUTH_TOKEN="${KEY}"`,
    `export ANTHROPIC_MODEL="${model}"`,
    `export ANTHROPIC_DEFAULT_HAIKU_MODEL="${small}"`,
    '',
    '# lalu jalankan seperti biasa',
    'claude',
  ].join('\n');
}

/** Claude first, else catalog default. */
function useAgentModels(): { main: string; small: string } {
  const { models, model } = useDocs();
  const has = (id: string) => models?.some((m) => m.id === id) ?? false;
  const main = has('claude-sonnet-5-5') ? 'claude-sonnet-5-5' : model;
  const small = has('claude-haiku-4-5') ? 'claude-haiku-4-5' : main;
  return { main, small };
}

export function ClaudeCodePage() {
  const { main, small } = useAgentModels();
  const [setup, setSetup] = useState<CcSetup>('shell');

  return (
    <>
      <PageHead slug="claude-code">
        Claude Code berjalan langsung dengan saldo Partner AI. Cukup arahkan alamat API-nya ke kami. Bisa di terminal, ekstensi VS Code,
        dan JetBrains.
      </PageHead>

      <DocSection id="pasang" title="Sambungkan">
        <ol>
          <li>
            Buat API key di <AppLink to="/api-keys">menu API Key</AppLink>.
          </li>
          <li>
            Isi variabel berikut. Pilih <b>Terminal</b> untuk satu komputer, atau <b>settings.json</b> (
            <code>~/.claude/settings.json</code>) agar terminal dan ekstensi editor memakai pengaturan yang sama.
          </li>
          <li>
            Jalankan <code>claude</code>. Selesai.
          </li>
        </ol>
        <CodeBlock code={claudeCodeSnippet(setup, main, small)} tabs={<Tabs tight tabs={CC_SETUP} value={setup} onChange={setSetup} />} />
        <Callout title="Tanpa /v1">
          Isi <code>ANTHROPIC_BASE_URL</code> dengan <code>{API_ORIGIN}</code> saja. Claude Code menambahkan <code>/v1/messages</code>{' '}
          sendiri.
        </Callout>
        <p>
          <code>ANTHROPIC_API_KEY</code> juga bisa dipakai sebagai ganti <code>ANTHROPIC_AUTH_TOKEN</code>. Bila sebelumnya Anda masuk
          dengan akun Claude dan muncul peringatan bentrok login, keluar dulu dengan <code>/logout</code>.
        </p>
      </DocSection>

      <DocSection id="model" title="Pilih model">
        <DocTable
          className="docs-fields"
          head={['Variabel', 'Fungsinya']}
          rows={[
            { key: 'main', cells: [<code>ANTHROPIC_MODEL</code>, 'Model utama untuk percakapan dan menulis kode.'] },
            {
              key: 'tiers',
              cells: [
                <code>ANTHROPIC_DEFAULT_SONNET_MODEL</code>,
                <>
                  Model yang dipakai saat Anda memilih Sonnet di <code>/model</code>. Ada juga <code>ANTHROPIC_DEFAULT_OPUS_MODEL</code>.
                </>,
              ],
            },
            {
              key: 'small',
              cells: [<code>ANTHROPIC_DEFAULT_HAIKU_MODEL</code>, 'Model ringan untuk tugas latar, misalnya membuat judul sesi. Pilih yang murah.'],
            },
          ]}
        />
        <p>
          Isi dengan ID mana pun dari <DocLink slug="model" hash="katalog">daftar model</DocLink>, termasuk GPT, Gemini, atau DeepSeek.
          Hasil terbaik tetap dari model Claude karena Claude Code dirancang untuknya. ID bertanggal seperti{' '}
          <code>claude-haiku-4-5-20251001</code> otomatis dicocokkan ke <code>claude-haiku-4-5</code>. Ganti model kapan saja dengan{' '}
          <code>/model &lt;id&gt;</code>.
        </p>
      </DocSection>

      <DocSection id="fitur" title="Yang didukung">
        <DocTable
          head={['Fitur', 'Status']}
          rows={[
            { key: 'tools', cells: ['Tool bawaan (Bash, Read, Edit, Write, Grep, dan lainnya)', ok(true, 'semua model dengan tool calling')] },
            { key: 'agents', cells: ['Sub-agent, MCP, skills', ok(true, 'berjalan di komputer Anda seperti biasa')] },
            { key: 'thinking', cells: ['Mode berpikir (thinking)', ok(true, 'Claude, dan model lain yang punya reasoning')] },
            { key: 'cache', cells: ['Prompt caching', ok(true, 'model Claude: prompt berulang ditagih jauh lebih murah')] },
            { key: 'media', cells: ['Gambar dan PDF', ok(true, 'Claude, GPT, dan Gemini')] },
            { key: 'count', cells: ['Hitung token (/context)', ok(true, 'perkiraan')] },
            { key: 'search', cells: ['Tool WebSearch bawaan', ok(false, 'fitur server Anthropic. Pakai WebFetch atau MCP pencarian.')] },
          ]}
        />
      </DocSection>

      <DocSection id="biaya" title="Biaya">
        <p>
          Setiap request memotong saldo Rupiah Anda sesuai token yang dipakai, sama seperti request API lain. Lihat rinciannya di{' '}
          <AppLink to="/usage">Pemakaian</AppLink>.
        </p>
        <Callout title="Abaikan angka biaya di Claude Code">
          Angka di <code>/cost</code> dihitung Claude Code dengan harga dolar Anthropic, bukan tagihan Partner AI Anda.
        </Callout>
        <p>
          Supaya hemat: pakai model ringan untuk <code>ANTHROPIC_DEFAULT_HAIKU_MODEL</code>, dan biarkan prompt caching bekerja. Claude
          Code menandai bagian prompt yang berulang, dan untuk model Claude bagian itu ditagih dengan tarif cache yang jauh lebih murah.
        </p>
      </DocSection>

      <DocSection id="masalah" title="Kalau ada masalah">
        <DocTable
          head={['Pesan', 'Penyebab dan solusi']}
          rows={[
            { key: '401', cells: [<code>401 authentication_error</code>, 'API key salah, dicabut, atau variabelnya belum terbaca. Buka terminal baru setelah mengubah ~/.zshrc.'] },
            { key: '402', cells: [<code>402 billing_error</code>, <>Saldo habis. <AppLink to="/wallet">Isi saldo</AppLink>, lalu ulangi.</>] },
            { key: '404', cells: [<code>404 not_found_error</code>, 'ID model tidak ada di katalog. Cek ejaannya di halaman Model.'] },
            { key: 'url', cells: ['Koneksi gagal', <>Pastikan <code>ANTHROPIC_BASE_URL</code> tanpa <code>/v1</code> dan tanpa garis miring di akhir.</>] },
          ]}
        />
      </DocSection>
    </>
  );
}

/* Codex */

function codexSnippet(model: string): string {
  return [
    '# ~/.codex/config.toml',
    `model = "${model}"`,
    'model_provider = "partnerai"',
    '',
    '[model_providers.partnerai]',
    'name = "Partner AI"',
    `base_url = "${BASE_URL}"`,
    `env_key = "${API_KEY_ENV}"`,
    'wire_api = "responses"',
    '',
    '# opsional: profil model lain',
    '[profiles.claude]',
    'model = "claude-sonnet-5-5"',
    'model_provider = "partnerai"',
  ].join('\n');
}

export function CodexPage() {
  const { models, model } = useDocs();
  const gpt = models?.find((m) => m.provider === 'openai' && (m.modality ?? 'text') === 'text')?.id ?? model;

  return (
    <>
      <PageHead slug="codex">
        Codex dari OpenAI bisa memakai model apa pun di Partner AI lewat <DocLink slug="responses">Responses API</DocLink> kami.
      </PageHead>

      <DocSection id="pasang" title="Sambungkan">
        <ol>
          <li>
            Buat API key di <AppLink to="/api-keys">menu API Key</AppLink>.
          </li>
          <li>
            Tambahkan provider Partner AI ke <code>~/.codex/config.toml</code>.
          </li>
          <li>Simpan key di variabel lingkungan, lalu jalankan Codex.</li>
        </ol>
        <CodeBlock label="config.toml" code={codexSnippet(gpt)} />
        <CodeBlock label="Terminal" code={[`export ${API_KEY_ENV}="${KEY}"`, 'codex', '', '# memakai profil lain', 'codex --profile claude'].join('\n')} />
      </DocSection>

      <DocSection id="model" title="Pilih model">
        <p>
          Isi <code>model</code> dengan ID dari <DocLink slug="model" hash="katalog">daftar model</DocLink>. Model GPT paling cocok karena
          Codex dirancang untuknya, tetapi Claude, Gemini, dan DeepSeek juga bisa. Untuk model di luar OpenAI, Codex kadang menampilkan
          peringatan <i>model metadata not found</i>. Peringatan itu aman diabaikan.
        </p>
      </DocSection>

      <DocSection id="fitur" title="Yang didukung">
        <DocTable
          head={['Fitur', 'Status']}
          rows={[
            { key: 'shell', cells: ['Menjalankan perintah (exec_command, shell)', ok(true, 'semua model dengan tool calling')] },
            { key: 'patch', cells: ['Mengedit file (apply_patch)', ok(true, 'termasuk tool custom dan namespace')] },
            { key: 'reasoning', cells: ['Ringkasan berpikir', ok(true, 'model yang punya reasoning')] },
            { key: 'images', cells: ['Gambar', ok(true, 'model yang menerima gambar')] },
            { key: 'search', cells: ['Tool web_search bawaan', ok(false, 'fitur server OpenAI')] },
          ]}
        />
      </DocSection>

      <DocSection id="masalah" title="Kalau ada masalah">
        <DocTable
          head={['Pesan', 'Penyebab dan solusi']}
          rows={[
            { key: 'env', cells: ['Missing environment variable', <>Variabel <code>{API_KEY_ENV}</code> belum diisi di terminal yang sama.</>] },
            { key: '401', cells: [<code>401</code>, 'API key salah atau sudah dicabut.'] },
            { key: '402', cells: [<code>402</code>, <>Saldo habis. <AppLink to="/wallet">Isi saldo</AppLink>.</>] },
            { key: 'wire', cells: ['Format tidak dikenali', <>Pastikan <code>wire_api = &quot;responses&quot;</code> dan <code>base_url</code> diakhiri <code>/v1</code>.</>] },
          ]}
        />
      </DocSection>
    </>
  );
}

/* Cline, Roo Code, Kilo Code */

export function ClinePage() {
  const { model } = useDocs();
  const { main } = useAgentModels();

  return (
    <>
      <PageHead slug="cline">
        Ketiga ekstensi VS Code ini punya pilihan <b>OpenAI Compatible</b>, jadi Partner AI bisa langsung dipasang. Proses berpikir model
        ikut tampil.
      </PageHead>

      <DocSection id="pasang" title="Sambungkan">
        <ol>
          <li>
            Buat API key di <AppLink to="/api-keys">menu API Key</AppLink>.
          </li>
          <li>Buka pengaturan ekstensi (ikon roda gigi di panelnya).</li>
          <li>
            Di <b>API Provider</b>, pilih <b>OpenAI Compatible</b>, lalu isi tiga kolom berikut.
          </li>
        </ol>
        <CodeBlock
          label="Pengaturan"
          code={['API Provider : OpenAI Compatible', `Base URL     : ${BASE_URL}`, `API Key      : ${KEY}`, `Model ID     : ${model}`].join('\n')}
        />
        <p>Roo Code dan Kilo Code menampilkan daftar model sendiri setelah API key diisi. Di Cline, ketik ID model secara manual.</p>
      </DocSection>

      <DocSection id="claude" title="Model Claude: jalur Anthropic">
        <p>
          Untuk model Claude, pilih <b>Anthropic</b> sebagai API Provider, centang <b>Use custom base URL</b>, lalu isi alamat berikut.
          Lewat jalur ini prompt caching bekerja penuh, sehingga tugas panjang jauh lebih hemat.
        </p>
        <CodeBlock label="Pengaturan" code={['API Provider    : Anthropic', `Custom base URL : ${API_ORIGIN}`, `API Key         : ${KEY}`, `Model           : ${main}`].join('\n')} />
      </DocSection>

      <DocSection id="pengaturan" title="Pengaturan model">
        <p>
          Di mode OpenAI Compatible, sesuaikan <b>Context Window</b> dan <b>Max Output Tokens</b> dengan angka di{' '}
          <DocLink slug="model" hash="katalog">daftar model</DocLink>. Aktifkan dukungan gambar hanya untuk model yang menerima gambar.
          Isian harga di ekstensi hanya untuk perkiraan biaya; tagihan sebenarnya ada di <AppLink to="/usage">Pemakaian</AppLink>.
        </p>
      </DocSection>

      <DocSection id="masalah" title="Kalau ada masalah">
        <DocTable
          head={['Pesan', 'Penyebab dan solusi']}
          rows={[
            { key: '401', cells: [<code>401</code>, 'API key salah atau sudah dicabut.'] },
            { key: '404', cells: [<code>404</code>, <>Base URL harus diakhiri <code>/v1</code> untuk OpenAI Compatible, dan tanpa <code>/v1</code> untuk Anthropic.</>] },
            { key: 'image', cells: ['does not accept image input', 'Model yang dipilih tidak menerima gambar. Matikan dukungan gambar atau ganti model.'] },
            { key: '402', cells: [<code>402</code>, <>Saldo habis. <AppLink to="/wallet">Isi saldo</AppLink>.</>] },
          ]}
        />
      </DocSection>
    </>
  );
}
